import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import {
  getCartSubtotal,
  getShipping,
  getAllCreditValues,
  getCartVariants,
  flattenProducts,
  getCartTotal,
  getTaxLineItems,
} from '@nexus/fff-cart';
import get from 'lodash/get';
import find from 'lodash/find';
import { useFlags } from 'launchdarkly-react-client-sdk';
import Toast, { showErrorToast } from '@nexus/fff-ui-shared/Toast';

import { track } from 'utils/segment';
import {
  BATCH_RENEWAL_COMPONENT_CONTEXT,
  CADENCE_TYPE,
  checkBatchRenewal,
} from 'utils/subscriptions';
import Button from 'shared-components/Button/Button';
import { fetchCartData, updateCart } from 'services/api/carts';
import {
  getCredits,
  checkout,
  fetchInvoicePreview,
} from 'services/api/checkout';
import { getSubscribableProducts } from 'services/api/products';

import BatchAndRenewalNotification from 'shared-components/BatchAndRenewalNotification/BatchAndRenewalNotification';
import MobileHeader from './MobileHeader/MobileHeader';
import BillingAndShipping from './BillingAndShipping/BillingAndShipping';
import YourOrder from './YourOrder/YourOrder';
import OrderTotal from './OrderTotal/OrderTotal';
import EmptyCart from './EmptyCart/EmptyCart';
import ActFastMessage from './ActFastMessage/ActFastMessage';

import styles from './Checkout.module.css';

const UNITED_STATES = 'UNITED_STATES';
const SOCIAL_DISCOUNT = 0;
const ERROR_TOAST = {
  422: {
    title: 'Payment Unsuccessful',
    subtitle:
      'Your payment could not be processed. Please check your card and try again.',
  },
  460: {
    title: 'Update to Your Cart',
    subtitle:
      'Some items are no longer available. Please remove the item(s) before placing your order.',
  },
  503: {
    title: 'Order Unsuccessful',
    subtitle:
      'We’re sorry, this action is temporarily unavailable. Please wait an hour and try again.',
  },
  default: {
    title: 'Order Unsuccessful',
    subtitle:
      'We’re experiencing some technical difficulties. Please try again later.',
  },
};

const getProductIds = (products) =>
  Object.values(products).map((product) => product.legacyPostId);
const getTotalProducts = (products) => Object.values(products).length;
const getTotalUnits = (variantQuantities) =>
  Object.values(variantQuantities).reduce(
    (result, quantity) => result + quantity,
    0
  );
const getUnavailableProducts = (cartVariants) =>
  !!find(cartVariants, (variant) => variant.quantity > variant.inventoryLimit);

const splitCarts = (cartVariants, variantCadenceTypes) => {
  const cartOneTimePurchase = [];
  const cartSubscriptions = [];

  cartVariants.forEach((item) => {
    if (item.id in variantCadenceTypes) {
      item.cadence = variantCadenceTypes[item.id];
      cartSubscriptions.push(item);
    } else {
      cartOneTimePurchase.push(item);
    }
  });

  return { cartOneTimePurchase, cartSubscriptions };
};

const Checkout = ({
  shippingAddress,
  isDesktop,
  trackingData,
  receiveOrderConfirmation,
  userId,
  campaignId,
}) => {
  const history = useHistory();
  const [cartData, setCartData] = useState({
    cartVariants: [],
    cartDetails: {},
    cartId: null,
    isLoading: true,
  });
  const [initialCredits, setInitialCredits] = useState({
    purchaseCredit: 0,
  });
  const [subscribableProducts, setSubscribableProducts] = useState({});
  const { shoppingCredit: shoppingCreditFF } = useFlags();
  const { alternateSubscriptions: alternateSubscriptionsFF } = useFlags();
  const [isProcessingCheckout, setIsProcessingCheckout] = useState(false);
  const [taxAmount, setTaxAmount] = useState(0);
  const [taxLoading, setTaxLoading] = useState(true);
  const [taxError, setTaxError] = useState(false);
  const { cartVariants, cartDetails, cartId, isLoading } = cartData;

  const subtotal = useMemo(() => getCartSubtotal(cartVariants), [cartVariants]);

  const { shippingAmount, additionalShippingAmount } = useMemo(
    () => getShipping(cartVariants, cartDetails, subtotal),
    [cartVariants, cartDetails]
  );

  const shippingTotal = useMemo(
    () => shippingAmount + additionalShippingAmount,
    [shippingAmount, additionalShippingAmount]
  );
  const credits = useMemo(
    () =>
      getAllCreditValues(
        subtotal,
        initialCredits,
        SOCIAL_DISCOUNT,
        'SHOP',
        shoppingCreditFF,
        shippingTotal
      ),
    [subtotal, initialCredits, shoppingCreditFF, shippingTotal]
  );

  const total = useMemo(() => {
    return getCartTotal(
      subtotal,
      initialCredits,
      0,
      'SHOP',
      false,
      taxAmount,
      shippingTotal,
      shoppingCreditFF
    );
  }, [subtotal, initialCredits, taxAmount, shoppingCreditFF, shippingTotal]);

  const unavailableProducts = getUnavailableProducts(cartVariants);
  const disabledButton = unavailableProducts || isProcessingCheckout;
  const userCountry = shippingAddress?.country;

  const displayTax = userCountry !== UNITED_STATES;

  // call update cart endpoint multiple times
  const removeItems = async (variantId, num) => {
    const params = {
      campaignType: 'SHOP',
      operation: 'REMOVE',
    };
    let apiCalls = num;
    let data = {};
    while (apiCalls > 0) {
      // eslint-disable-next-line no-await-in-loop
      const response = await updateCart(variantId, params);
      data = response.value;
      apiCalls -= 1;
    }
    return data;
  };
  // adjust cart items according to inventory limit
  const updateQuantity = async (
    variantId,
    quantity,
    inventoryLimit,
    setUpdating
  ) => {
    const num = quantity - inventoryLimit;
    try {
      setUpdating(true);
      const { products: updatedProducts, cartDetails: updatedCartDetails } =
        await removeItems(variantId, num);
      const { products, variants } = flattenProducts(updatedProducts);
      const updatedCartVariants = getCartVariants(
        updatedCartDetails,
        variants,
        products
      );
      setCartData((prevState) => ({
        ...prevState,
        cartVariants: updatedCartVariants,
        cartDetails: updatedCartDetails,
      }));
      setUpdating(false);
    } catch (error) {
      const { default: title, subtitle } = ERROR_TOAST;
      setUpdating(false);
      showErrorToast(title, subtitle);
    }
  };
  const handleTracking = (invoice) => {
    track('Order Completed', {
      ...trackingData,
      cart_tab: 'Shop',
      cta_name: 'PLACE ORDER',
      subtotal: invoice.subtotal,
      total: invoice.total,
      shipping: invoice.shippingFee,
      discount: invoice.purchaseCreditApplied,
      currency: 'USD',
      products: getProductIds(invoice.products),
      total_products: getTotalProducts(invoice.products),
      total_units: getTotalUnits(invoice.variantQuantities),
    });
  };
  const handleClickSubmit = async (e) => {
    e.preventDefault();
    try {
      setIsProcessingCheckout(true);
      const response = await checkout(cartId);
      if (response.ok) {
        const invoice = await response.json();
        receiveOrderConfirmation(invoice);
        handleTracking(invoice);
        history.push('/shop/order-confirmation');
      } else {
        throw new Error(response.status);
      }
    } catch (error) {
      const errorMessage = get(ERROR_TOAST, error.message, ERROR_TOAST.default);
      if (error.message === '460') {
        // refetch cart data if the API returns a Shop Inventory error (status = 460) if quantity > inventoryLimit
        const {
          cartDetails: updatedCartDetails,
          cartVariants: updatedCartVariants,
        } = await fetchCartData();
        setCartData((prevState) => ({
          ...prevState,
          cartDetails: updatedCartDetails,
          cartVariants: updatedCartVariants,
        }));
      }
      setIsProcessingCheckout(false);
      showErrorToast(errorMessage.title, errorMessage.subtitle);
    }
  };

  useEffect(() => {
    Promise.all([
      fetchCartData(),
      getCredits(),
      alternateSubscriptionsFF && getSubscribableProducts(),
    ]).then(([cartResponse, creditsResponse, subscriptionResponse]) => {
      const { cartDetails, cartVariants, cartId } = cartResponse;
      setInitialCredits(creditsResponse);
      setCartData({
        cartVariants,
        cartDetails,
        cartId,
        isLoading: false,
      });
      alternateSubscriptionsFF && setSubscribableProducts(subscriptionResponse);
    });
  }, []);

  useEffect(() => {
    const getTax = async (payload) => {
      try {
        const response = await fetchInvoicePreview(payload);
        const { tax } = response;
        setTaxAmount(tax);
      } catch {
        setTaxError(true);
      }
      setTaxLoading(false);
    };
    const lineItems = getTaxLineItems(
      cartVariants,
      shippingAmount,
      additionalShippingAmount
    );
    const payload = {
      shopUserId: userId,
      campaignId,
      campaignType: 'shop',
      currencyCode: 'USD',
      windowType: 0,
      lineItems,
    };
    if (displayTax && cartVariants.length && campaignId) {
      getTax(payload);
    } else {
      setTaxLoading(false);
    }
  }, [cartVariants, shippingAmount, additionalShippingAmount, campaignId]);

  if (isLoading || taxLoading) return null;

  const { cartOneTimePurchase, cartSubscriptions } = splitCarts(
    cartData.cartVariants,
    cartData.cartDetails.variantCadenceTypes
  );

  const nextRenewalDate = checkBatchRenewal(
    subscribableProducts,
    CADENCE_TYPE.NINETY_DAYS
  );

  return (
    <>
      <div className={styles.checkout}>
        {isDesktop && (
          <div className={styles.freeShipping}>
            FREE SHIPPING ON U.S. CONTIGUOUS ORDERS OVER $49
          </div>
        )}
        {cartVariants.length ? (
          <div className={styles.container}>
            {isDesktop ? <h1>Checkout</h1> : <MobileHeader />}
            <div className={styles.content}>
              {shippingAddress && (
                <BillingAndShipping
                  shippingAddress={shippingAddress}
                  credits={credits}
                />
              )}
              <div className={styles.yourOrder}>
                {cartOneTimePurchase.length > 0 && (
                  <YourOrder
                    cartVariants={cartOneTimePurchase}
                    updateQuantity={updateQuantity}
                    title="Your Order"
                  />
                )}
                {cartSubscriptions.length > 0 && (
                  <>
                    <YourOrder
                      cartVariants={cartSubscriptions}
                      updateQuantity={updateQuantity}
                      title="Your Subscriptions"
                    />
                    {nextRenewalDate && (
                      <BatchAndRenewalNotification
                        shippingDate={nextRenewalDate}
                        cadenceType={CADENCE_TYPE.NINETY_DAYS}
                        context={BATCH_RENEWAL_COMPONENT_CONTEXT.checkout}
                      />
                    )}
                  </>
                )}
                <ActFastMessage />
                <OrderTotal
                  subtotal={subtotal}
                  appliedPurchaseCredit={credits.appliedPurchaseCredit}
                  hasAppliedPurchaseCredit={credits.hasAppliedPurchaseCredit}
                  shippingPrice={shippingAmount}
                  additionalShipping={additionalShippingAmount}
                  tax={taxAmount}
                  displayTax={displayTax}
                  country={userCountry}
                  total={total}
                  taxError={taxError}
                  showNote
                  showSubscriptionDisclaimer={cartSubscriptions.length > 0}
                  appliedShoppingCredit={credits.appliedShoppingCredit}
                  hasAppliedShoppingCredit={credits.hasAppliedShoppingCredit}
                />
                <form className={styles.confirm} onSubmit={handleClickSubmit}>
                  <div
                    data-tip="Please agree with the Terms & Conditions to place your order."
                    data-tip-disable={isProcessingCheckout}
                  >
                    <Button
                      type="submit"
                      className={styles.submitButton}
                      disabled={disabledButton}
                    >
                      <span
                        className={
                          isProcessingCheckout ? styles.processing : ''
                        }
                      >
                        {isProcessingCheckout ? 'Processing' : 'Place Order'}
                      </span>
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        ) : (
          <div className={styles.container}>
            {!isDesktop && <MobileHeader />}
            <EmptyCart />
          </div>
        )}
      </div>
      <Toast />
    </>
  );
};

Checkout.propTypes = {
  shippingAddress: PropTypes.shape({}).isRequired,
  isDesktop: PropTypes.bool.isRequired,
  trackingData: PropTypes.shape({}).isRequired,
  receiveOrderConfirmation: PropTypes.func.isRequired,
  userId: PropTypes.number.isRequired,
  campaignId: PropTypes.number,
};

Checkout.defaultProps = {
  campaignId: null,
};

export default Checkout;
